<template>
  <div class="product-detail-page" v-loading="loading">
    <div class="detail-box">
      <!-- 标题区域 -->
      <div class="header-section">
        <h1 class="title">{{ productIntroductionData.displayName }}</h1>
        <p v-if="productIntroductionData.remark" class="subtitle">{{ productIntroductionData.remark }}</p>
      </div>

      <!-- 视频介绍（微信内优化播放） -->
      <div class="video-section" v-if="productIntroductionData.videoPath">
        <div class="video-wrapper">
          <video
            ref="videoPlayer"
            class="video-player"
            :src="videoUrl"
            controls
            preload="metadata"
            playsinline
            webkit-playsinline
            x5-playsinline
            x5-video-player-type="h5"
            x5-video-player-fullscreen="true"
            x5-video-orientation="portraint"
            controlslist="nodownload"
          ></video>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="content-wrapper" v-if="productIntroductionData.fileContent">
        <div class="news-content" v-html="productIntroductionData.fileContent"/>
      </div>
    </div>
  </div>
</template>

<script>
import homeApi from '@/api/home'

export default {
  name: 'ProductIntroductionDetail',
  data () {
    return {
      id: this.$route.params.id,
      productIntroductionData: {},
      // 初始为 true，进入页面即显示 loading，数据返回后隐藏
      loading: true
    }
  },
  mounted () {
    this.loadProductDetail()
    window.scrollTo(0, 0)
  },
  computed: {
    // http 视频地址转 https：避免微信内置浏览器拦截混合内容导致视频无法播放
    videoUrl () {
      const path = this.productIntroductionData.videoPath
      if (!path) {
        return ''
      }
      return path.replace(/^http:\/\//, 'https://')
    }
  },
  methods: {
    // 加载产品详情
    loadProductDetail () {
      if (!this.id) {
        this.loading = false
        this.$message.warning('该产品暂无详情信息')
        return
      }
      this.loading = true
      // 注意：不用 Promise.finally，老版本微信 X5 内核不支持，会导致 loading 遮罩永不关闭（页面空白）
      // 15 秒超时兜底：避免微信冷启动时请求挂起导致 loading 永不关闭
      homeApi.getProductIntroductionDetail({ id: this.id }, { timeout: 15000 }).then(res => {
        if (res) {
          this.productIntroductionData = res
          // 动态设置页面标题，便于微信分享卡片抓取产品名称
          if (res.displayName) {
            document.title = res.displayName
          }
        }
        this.loading = false
      }).catch(err => {
        console.error('加载产品介绍失败:', err)
        this.$message.error('加载产品介绍失败')
        this.loading = false
      })
    }
  }
}
</script>

<style scoped lang="less">
.product-detail-page {
  width: 100%;
  min-height: 100vh;
  background: #f5f7fa;
  padding: 10px;
  box-sizing: border-box;

  .detail-box {
    max-width: 750px;
    margin: 0 auto;
    background: #ffffff;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);

    .header-section {
      padding: 22px 16px 16px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #ffffff;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #f093fb 0%, #f5576c 100%);
      }

      .title {
        font-size: 20px;
        font-weight: 600;
        margin: 0 0 8px 0;
        text-align: center;
        line-height: 1.5;
        word-break: break-all;
      }

      .subtitle {
        font-size: 13px;
        text-align: center;
        margin: 0;
        opacity: 0.9;
        line-height: 1.7;
        word-break: break-all;
      }
    }

    // 视频区块
    .video-section {
      padding: 16px 12px 0;
      background: #ffffff;

      .video-wrapper {
        border-radius: 6px;
        overflow: hidden;
        background: #000;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);

        .video-player {
          display: block;
          width: 100%;
          max-height: 45vh;
          object-fit: contain;
          background: #000;
        }
      }
    }

    .content-wrapper {
      padding: 16px 12px 20px;
      background: #ffffff;
      min-height: 200px;
      overflow-x: auto;
      box-sizing: border-box;

      .news-content {
        line-height: 1.9;
        font-size: 14.5px;
        color: #333333;
        letter-spacing: 0.3px;
        word-wrap: break-word;
        overflow-wrap: break-word;

        ::v-deep {
          img {
            max-width: 100%;
            height: auto;
            border-radius: 4px;
            margin: 12px 0;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          }

          p {
            margin: 12px 0;
            text-align: justify;
          }

          h1, h2, h3, h4, h5, h6 {
            color: #2c3e50;
            margin: 18px 0 12px;
            font-weight: 600;
            line-height: 1.5;
            word-break: break-all;
          }

          ul, ol {
            padding-left: 20px;
            margin: 12px 0;
          }

          li {
            margin: 8px 0;
            line-height: 1.8;
          }

          a {
            color: #667eea;
            text-decoration: none;
            word-break: break-all;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin: 16px 0;

            th, td {
              border: 1px solid #e0e0e0;
              padding: 8px;
              text-align: left;
              line-height: 1.6;
              font-size: 13px;
              word-break: break-all;
            }

            th {
              background: #f5f7fa;
              font-weight: 600;
              color: #2c3e50;
            }
          }

          blockquote {
            border-left: 4px solid #667eea;
            padding: 12px 14px;
            margin: 14px 0;
            background: #f8f9ff;
            border-radius: 4px;
            color: #555;
            line-height: 1.8;
          }

          code {
            background: #f4f4f4;
            padding: 2px 6px;
            border-radius: 3px;
            font-family: 'Courier New', monospace;
            color: #e83e8c;
            word-break: break-all;
          }

          pre {
            background: #2d2d2d;
            color: #f8f8f2;
            padding: 18px;
            border-radius: 6px;
            overflow-x: auto;
            margin: 16px 0;
            line-height: 1.6;

            code {
              background: transparent;
              color: inherit;
              padding: 0;
            }
          }

          iframe {
            max-width: 100%;
          }

          video {
            max-width: 100%;
          }
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/productIntroductionDetail/index.vue